import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Field, Notice, Select } from '../../shell/kit';
import { useToast } from '../../shell/toast';
import { useConfirm } from '../../shell/confirm';
import { errorText } from '../../shell/errors';
import {
  listTemplates,
  saveTemplate,
  templateCatalog,
  type CommsTemplate,
  type SaveTemplateInput,
  type TemplatePurpose,
} from './api';
import type { CommsKey, CommsLocale, makeCommsT } from './i18n';

/**
 * Редактор шаблонов сообщений.
 *
 * Экран переписан 04.09.2026 под человека, а не под разработчика. Было так:
 * код шаблона вводился строкой (надо было знать, что ответ на обращение зовётся
 * `feedback.answer`), вид сообщения выбирался рядом с кодом, подстановки
 * перечислялись через запятую отдельно от текста, канал и язык показывались как
 * `auto` и `kk`, увидеть будущее сообщение было негде, а открыть существующий
 * шаблон и поправить в нём слово — нельзя вовсе: форма только создавала новый.
 *
 * Стало похоже на работу с файлами: слева список шаблонов, клик открывает текст
 * в редакторе справа. Назначение объясняет само, когда сообщение уходит;
 * подстановки вставляются кнопками по курсору и считаются из текста; рядом
 * предпросмотр с настоящими примерами и счётчик сегментов SMS.
 *
 * Сохранение заводит новую версию, а не переписывает старую — так устроен
 * `upsertTemplate`, и это правильно: текст, который уже ушёл клиентам, должен
 * остаться в истории. Экран об этом прямо говорит.
 *
 * Вид сообщения из формы убран совсем. Он следует из назначения, и это не
 * упрощение: на него смотрит гейт согласия — `service` уходит без
 * маркетингового согласия. Пока вид был свободным выбором, рекламный шаблон
 * можно было завести служебным и разослать по всей базе в обход согласий.
 */

type Translate = ReturnType<typeof makeCommsT>;

const CHANNELS: readonly { readonly value: SaveTemplateInput['channel']; readonly key: CommsKey }[] =
  [
    { value: 'auto', key: 'comms.channel.auto' },
    { value: 'sms', key: 'comms.channel.sms' },
    { value: 'whatsapp', key: 'comms.channel.whatsapp' },
    { value: 'push', key: 'comms.channel.push' },
    { value: 'email', key: 'comms.channel.email' },
  ];

/**
 * Подстановки, встреченные в тексте.
 *
 * Повторяет `variablesInBody` из модуля comms намеренно: тащить серверный пакет
 * в браузер ради одной регулярки дороже, чем повторить её. Расхождение поймает
 * сервер — он сверяет объявленные подстановки с текстом.
 */
function variablesInBody(body: string): readonly string[] {
  const found = new Set<string>();
  for (const match of body.matchAll(/\{([a-zA-Z][a-zA-Z0-9_]*)\}/g)) {
    const name = match[1];
    if (name) found.add(name);
  }
  return [...found];
}

/**
 * Длина в сегментах SMS. Кириллица кодируется UCS-2: 70 символов в одном
 * сегменте и по 67 в каждом при нескольких. Платят посегментно, и перерасход
 * человек должен видеть до отправки, а не в счёте.
 */
function smsSegments(body: string): { readonly length: number; readonly segments: number } {
  const length = [...body].length;
  if (length === 0) return { length: 0, segments: 0 };
  const ucs2 = [...body].some((ch) => (ch.codePointAt(0) ?? 0) > 127);
  const single = ucs2 ? 70 : 160;
  const multi = ucs2 ? 67 : 153;
  return { length, segments: length <= single ? 1 : Math.ceil(length / multi) };
}

/** Подставляет примеры вместо `{name}` — так видно, что придёт клиенту. */
function preview(body: string, purpose: TemplatePurpose | undefined, locale: CommsLocale): string {
  if (!purpose) return body;
  let out = body;
  for (const v of purpose.variables) {
    out = out.replaceAll('{' + v.name + '}', locale === 'kk' ? v.exampleKk : v.exampleRu);
  }
  return out;
}

/** Последние версии: в списке нужен один шаблон на назначение, язык и канал. */
function latest(rows: readonly CommsTemplate[]): readonly CommsTemplate[] {
  const best = new Map<string, CommsTemplate>();
  for (const row of rows) {
    const key = row.code + '|' + row.locale + '|' + row.channel;
    const current = best.get(key);
    if (!current || row.version > current.version) best.set(key, row);
  }
  return [...best.values()].sort((a, b) => a.code.localeCompare(b.code) || a.locale.localeCompare(b.locale));
}

interface Draft {
  readonly code: string;
  readonly channel: SaveTemplateInput['channel'];
  readonly locale: 'ru' | 'kk';
  readonly body: string;
  /** Версия, от которой пляшем. `0` — шаблона ещё нет. */
  readonly version: number;
}

const BLANK: Draft = { code: '', channel: 'auto', locale: 'ru', body: '', version: 0 };

export function TemplatesView({ t, locale }: { t: Translate; locale: CommsLocale }) {
  const [rows, setRows] = useState<readonly CommsTemplate[]>([]);
  const [purposes, setPurposes] = useState<readonly TemplatePurpose[]>([]);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const toast = useToast();
  const confirm = useConfirm();

  const fail = useCallback(
    (err: unknown) => {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    },
    [toast],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, catalog] = await Promise.all([listTemplates(), templateCatalog()]);
      setRows(list.templates);
      setPurposes(catalog.purposes);
      setDraft((current) =>
        current.code ? current : { ...BLANK, code: catalog.purposes[0]?.code ?? '' },
      );
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, [fail]);

  useEffect(() => {
    void load();
  }, [load]);

  const files = latest(rows);
  const purpose = purposes.find((p) => p.code === draft.code);

  /*
   * История именно этого шаблона: то же назначение, тот же язык, тот же канал.
   * Сервер отдаёт все версии сразу, поэтому отдельного запроса не нужно.
   */
  const history = rows
    .filter(
      (r) => r.code === draft.code && r.locale === draft.locale && r.channel === draft.channel,
    )
    .sort((a, b) => b.version - a.version);
  const sms = smsSegments(preview(draft.body, purpose, draft.locale));
  const purposeTitle = (p: TemplatePurpose | undefined, code: string): string =>
    p ? (locale === 'kk' ? p.titleKk : p.titleRu) : code;

  // Сброс несохранённого текста подтверждается диалогом приложения, а не
  // нативным окном браузера (QA.md, Q25).
  async function mayDropChanges(): Promise<boolean> {
    return !dirty || (await confirm({ title: t('comms.templates.dropChanges') }));
  }

  async function open(row: CommsTemplate): Promise<void> {
    if (!(await mayDropChanges())) return;
    setDraft({
      code: row.code,
      channel: row.channel,
      locale: row.locale,
      body: row.body,
      version: row.version,
    });
    setDirty(false);
  }

  async function startNew(): Promise<void> {
    if (!(await mayDropChanges())) return;
    setDraft({ ...BLANK, code: purposes[0]?.code ?? '' });
    setDirty(false);
  }

  function edit(next: Partial<Draft>): void {
    setDraft((current) => ({ ...current, ...next }));
    setDirty(true);
  }

  /** Вставка подстановки туда, где стоит курсор, а не в конец текста. */
  function insert(name: string): void {
    const field = bodyRef.current;
    const token = '{' + name + '}';
    if (!field) {
      edit({ body: draft.body + token });
      return;
    }
    const from = field.selectionStart ?? draft.body.length;
    const to = field.selectionEnd ?? from;
    edit({ body: draft.body.slice(0, from) + token + draft.body.slice(to) });
    requestAnimationFrame(() => {
      field.focus();
      field.setSelectionRange(from + token.length, from + token.length);
    });
  }

  async function save(): Promise<void> {
    if (!draft.code || !draft.body.trim()) {
      toast.warn(t('comms.common.required'));
      return;
    }
    try {
      const saved = await saveTemplate({
        code: draft.code,
        // Вид не спрашиваем: он следует из назначения, и сервер расхождение
        // отклонит. От него зависит проверка согласия — это не поле формы.
        kind: purpose?.kind ?? 'service',
        channel: draft.channel,
        locale: draft.locale,
        body: draft.body,
        // Подстановки считаются из текста: перечислять их руками — значит
        // разойтись с текстом при первой же правке.
        variables: [...variablesInBody(draft.body)],
      });
      toast.ok(t('comms.templates.savedVersion', { version: String(saved.version) }));
      setDraft((current) => ({ ...current, version: saved.version }));
      setDirty(false);
      await load();
    } catch (err) {
      fail(err);
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
      <Card title={t('comms.templates.title')}>
        <div className="mb-2">
          <Button onClick={() => void startNew()}>{t('comms.templates.new')}</Button>
        </div>

        {loading ? <p className="text-ink-muted text-sm">{t('comms.common.loading')}</p> : null}
        {!loading && files.length === 0 ? (
          <p className="text-ink-muted text-sm">{t('comms.common.nothing')}</p>
        ) : null}

        <ul className="flex flex-col gap-1">
          {files.map((row) => {
            const p = purposes.find((x) => x.code === row.code);
            const active =
              row.code === draft.code &&
              row.locale === draft.locale &&
              row.channel === draft.channel;
            return (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => void open(row)}
                  className={`w-full rounded-control border px-3 py-2 text-left transition ${
                    active
                      ? 'border-brand-300 bg-brand-50'
                      : 'border-transparent hover:border-line hover:bg-sunken'
                  }`}
                >
                  <div className="text-sm text-ink">{purposeTitle(p, row.code)}</div>
                  <div className="text-ink-muted text-xs">
                    {t(('comms.locale.' + row.locale) as CommsKey)} ·{' '}
                    {t(('comms.channel.' + row.channel) as CommsKey)} ·{' '}
                    {t('comms.templates.versionShort', { version: String(row.version) })}
                  </div>
                  <div className="text-ink-faint mt-1 line-clamp-2 text-xs">{row.body}</div>
                </button>
              </li>
            );
          })}
        </ul>
      </Card>

      <Card
        title={
          draft.version > 0
            ? purposeTitle(purpose, draft.code)
            : t('comms.templates.newTitle')
        }
      >
        <div className="grid gap-3 md:grid-cols-3">
          <Field label={t('comms.templates.purpose')} hint={t('comms.templates.purposeHint')}>
            <Select value={draft.code} onChange={(e) => edit({ code: e.target.value })}>
              {purposes.map((p) => (
                <option key={p.code} value={p.code}>
                  {purposeTitle(p, p.code)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('comms.templates.channel')} hint={t('comms.templates.channelHint')}>
            <Select
              value={draft.channel}
              onChange={(e) => edit({ channel: e.target.value as SaveTemplateInput['channel'] })}
            >
              {CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {t(c.key)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('comms.templates.locale')}>
            <Select
              value={draft.locale}
              onChange={(e) => edit({ locale: e.target.value as 'ru' | 'kk' })}
            >
              <option value="ru">{t('comms.locale.ru')}</option>
              <option value="kk">{t('comms.locale.kk')}</option>
            </Select>
          </Field>
        </div>

        {purpose ? (
          <div className="mt-3">
            <Notice tone={purpose.kind === 'marketing' ? 'warn' : 'info'}>
              <p>{locale === 'kk' ? purpose.whenKk : purpose.whenRu}</p>
              <p className="mt-1">
                {purpose.kind === 'marketing'
                  ? t('comms.templates.isMarketing')
                  : t('comms.templates.isService')}
              </p>
            </Notice>
          </div>
        ) : null}

        <div className="mt-3">
          <Field label={t('comms.templates.body')} hint={t('comms.templates.bodyHint')}>
            <textarea
              ref={bodyRef}
              value={draft.body}
              rows={6}
              spellCheck
              onChange={(e) => edit({ body: e.target.value })}
              className="w-full rounded-control border border-line-strong bg-surface px-3 py-2 font-mono text-sm text-ink"
            />
          </Field>
        </div>

        {purpose && purpose.variables.length > 0 ? (
          <div className="mt-2">
            <p className="text-ink-muted text-xs">{t('comms.templates.insertHint')}</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {purpose.variables.map((v) => (
                <Button key={v.name} size="sm" onClick={() => insert(v.name)}>
                  {locale === 'kk' ? v.titleKk : v.titleRu}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        {draft.body.trim() ? (
          <div className="mt-3 space-y-1">
            <p className="text-ink-muted text-xs">{t('comms.templates.preview')}</p>
            <div className="rounded-control border border-line bg-sunken px-3 py-2 text-sm whitespace-pre-wrap">
              {preview(draft.body, purpose, draft.locale)}
            </div>
            <p className="text-ink-muted text-xs">
              {t('comms.templates.length', {
                length: String(sms.length),
                segments: String(sms.segments),
              })}
            </p>
          </div>
        ) : null}

        {history.length > 1 ? (
          <div className="mt-4 border-t border-line pt-3">
            <p className="text-ink-muted text-xs">{t('comms.templates.history')}</p>
            <ul className="mt-1 flex flex-col gap-1">
              {history.map((row) => (
                <li key={row.id} className="flex flex-wrap items-baseline gap-2 text-xs">
                  <button
                    type="button"
                    className="text-brand-700 underline underline-offset-2"
                    onClick={() => void open(row)}
                  >
                    {t('comms.templates.versionShort', { version: String(row.version) })}
                  </button>
                  {row.version === draft.version ? (
                    <span className="text-ink-muted">{t('comms.templates.opened')}</span>
                  ) : null}
                  <span className="text-ink-faint truncate">{row.body}</span>
                </li>
              ))}
            </ul>
            <p className="text-ink-faint mt-1 text-xs">{t('comms.templates.historyHint')}</p>
          </div>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button tone="primary" onClick={() => void save()} disabled={!dirty}>
            {t('comms.templates.save')}
          </Button>
          <span className="text-ink-muted text-xs">
            {draft.version > 0
              ? t('comms.templates.willBump', { version: String(draft.version + 1) })
              : t('comms.templates.willCreate')}
          </span>
        </div>
      </Card>
    </div>
  );
}
